import localforage from 'localforage';
import {
  getUserOrganizationAccess,
  Organization,
  USER_STATE_IN_ORGANIZATION,
  UserOrganizationAccess,
} from '../api/organization';
import RoutePaths from '../router/RoutePaths';
import { LoginResponseModel } from '../api/user';
import { WorkspaceAccess } from '../types/WorkspaceAccess';
import { useQuery } from '@tanstack/react-query';
import Session from 'supertokens-auth-react/recipe/session';
import { signOut } from 'supertokens-auth-react/recipe/session';
import { analytics } from '../analytics/analytics';
import { QUERY_CONFIG } from '../constants/queryConfig';
import { queryClient } from '../queryClient';

/**
 * Do not directly write to this key in localstorage/indexdb.
 * Use helper method from an auth library.
 *
 * Reading it is fine, but there is also a helper methods for that.
 */
export const activeOrganizationKey = 'activeOrganization';

export const userOrganizationAccessKey = 'userOrganizationAccess';

export type AccessTokenUser = {
  user: {
    id: number;
    email: string;
    fullName: string;
    verifiedEmail: boolean;
    role: string;
    state: string;
    updatedAt: string;
    passwordSet: boolean;
    profilePicturePath: string;
    // Self-sort firmographics — carried in the JWT so completion survives reload.
    jobRole?: string | null;
    selfSortCompletedAt?: string | null;
  };
  organizationAccess: WorkspaceAccess[];
  iat: number;
  iss: string;
  aud: string;
  exp: number;
};

export async function getAccessToken(): Promise<string> {
  return (await Session.getAccessToken()) || '';
}

export enum OrganizationAccessRoles {
  ADMIN = 'Admin',
  MEMBER = 'Member',
}

export async function logoutUser() {
  await signOut();
  // Route user to the login page
  window.location.href = RoutePaths.Login;
  analytics.logout();
}

export async function getCurrentlyActiveOrganization(): Promise<Organization> {
  const org = await getOrganizationAccess();

  if (!org) {
    throw new Error('No active organization selected');
  }

  return org;
}

export const activeOrganizationQueryKey = ['currentOrgnizationFromLocalStorage'];

export const useActiveOrganization = () => {
  return useQuery<Organization, Error>({
    queryKey: activeOrganizationQueryKey,
    queryFn: async () => {
      return await getCurrentlyActiveOrganization();
    },
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

/**
 * Writes the active org and syncs the cached read in the same step.
 *
 * `useActiveOrganization` caches this value for 24h under a single key, and its only
 * observer (ModalProvider) sits above the router, so nothing ever remounts the query.
 * Org SWITCHING gets away with that because it hard-reloads the page; org CREATION
 * navigates via the SPA router, so without this write every consumer keeps reading the
 * org that was active at app boot — which made the self-sort modal post firmographics
 * to the wrong org, or to no resolvable org at all after a fresh signup (#2776).
 */
export async function setCurrentlyActiveOrganization(
  orgAccess: UserOrganizationAccess
): Promise<UserOrganizationAccess> {
  const stored = await localforage.setItem(activeOrganizationKey, orgAccess);
  queryClient.setQueryData<Organization>(activeOrganizationQueryKey, stored);
  return stored;
}

export async function getOrganizationAccess(): Promise<UserOrganizationAccess | null> {
  return await localforage.getItem(activeOrganizationKey);
}

export async function removeActiveOrganization() {
  const removed = await localforage.removeItem(activeOrganizationKey);
  queryClient.removeQueries({ queryKey: activeOrganizationQueryKey });
  return removed;
}

/**
 * This method stores all the organizations user has access to, allowing switching between them.
 * @param orgsAccess
 */
export async function setUsersOrganizationAccess(
  orgsAccess: UserOrganizationAccess[]
): Promise<UserOrganizationAccess[]> {
  return await localforage.setItem(userOrganizationAccessKey, orgsAccess);
}

/**
 * Returns all organizations that are currently stored within the localstorage that user has access to.
 */
export async function getUsersAccessibleOrganizations(): Promise<UserOrganizationAccess[]> {
  return (await localforage.getItem(userOrganizationAccessKey)) ?? [];
}

export async function loginAndRedirect(res: LoginResponseModel) {
  const userOrganizationAccess = await loginProcess(res);

  // navigate hook does not work outside components
  if (userOrganizationAccess.length === 1) {
    // We know user only has access to 1 org we log him into that one directly
    await setCurrentlyActiveOrganization(userOrganizationAccess[0]);

    const userOnboarded = isUserOnboardedToOrg(userOrganizationAccess);

    if (!userOnboarded) {
      window.location.href = `${RoutePaths.Onboarding.children.InvitedUserOnboarding}?orgId=${userOrganizationAccess[0].id}`;
    } else {
      window.location.href = RoutePaths.Home;
    }
  } else {
    // Here we know user has more than 1 org. So we give him a selection to make
    // navigate(RoutePaths.LoginSelectOrganization);
    window.location.href = RoutePaths.LoginSelectOrganization;
  }

  return;
}

export async function loginProcess(res: LoginResponseModel): Promise<UserOrganizationAccess[]> {
  const userOrganizationAccess = await getUserOrganizationAccess();

  await setUsersOrganizationAccess(userOrganizationAccess.data);

  return userOrganizationAccess.data;
}

function isUserOnboardedToOrg(userOrganizationAccess: UserOrganizationAccess[]): boolean {
  const userInOrg = userOrganizationAccess[0].OrganizationAccess.find(
    async (u) => u.userId === (await getUserFromAccessToken()).user.id
  );
  const userIsNotOnboarded = userInOrg?.usersStateInOrganization === USER_STATE_IN_ORGANIZATION.INVITED;

  return !userIsNotOnboarded;
}

/**
 *
 * @param {LoginResponseModel} res
 * @param {number} orgId
 * @returns {Promise<void>}
 */
export async function loginInvitedUser(res: LoginResponseModel, orgId: number): Promise<void> {
  const userOrganizationAccess = await loginProcess(res);

  window.location.href = RoutePaths.LoginSelectOrganization;

  // // navigate hook does not work outside components
  // if (userOrganizationAccess.length === 1) {
  //   // We know user only has access to 1 org we log him into that one directly
  //   await setCurrentlyActiveOrganization(userOrganizationAccess[0]);
  //   // Also check the state to know if we need to send him off to onboarding for that org
  //   const userOnboarded = isUserOnboardedToOrg(userOrganizationAccess);

  //   if (!userOnboarded) {
  //     await verifyUserInvitation(orgId)

  //     window.location.href = `${RoutePaths.Onboarding.children.InvitedUserOnboarding}?orgId=${orgId}`;
  //   } else {
  //     window.location.href = RoutePaths.Home;
  //   }
  // } else if (userOrganizationAccess.length === 0) {
  //   // Check if user was successfully added with an org. If not we default him to regular onboarding flow
  //   window.location.href = RoutePaths.LoginSelectOrganization;
  // } else if (userOrganizationAccess.length > 1) {
  //   // Here we know user has more than 1 org. So we give him a selection to make
  //   window.location.href = RoutePaths.LoginSelectOrganization;
  // }

  return;
}

export async function getUserFromAccessToken(): Promise<AccessTokenUser> {
  // let userId = await Session.getUserId();
  const token = (await Session.getAccessTokenPayloadSecurely()) as AccessTokenUser;
  return token;
}

export async function getCurrentUsersRoleInActiveOrganization() {
  const activeOrg = await getOrganizationAccess();
  const myUser = await getUserFromAccessToken();

  return activeOrg?.OrganizationAccess?.find((access) => access.userId === myUser.user.id)
    ?.roleToAssume as OrganizationAccessRoles;
}

export const useGetUserFromAccessToken = () => {
  const query = useQuery<AccessTokenUser, Error>({
    queryKey: ['userFromAccessToken'],
    queryFn: async () => {
      return await getUserFromAccessToken();
    },
    staleTime: QUERY_CONFIG.STALE_TIME,
  });

  return query;
};
